import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import DocumentSection from './DocumentSection';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ iconLeft, onClick, ...props }: any) => (
    <button data-icon={iconLeft} onClick={onClick} {...props} />
  ),
}));

jest.mock('components/general/confirmation_modal/ConfirmationModal', () => ({
  __esModule: true,
  default: ({ isOpen, onConfirm, onClose, title }: any) =>
    isOpen ? (
      <div data-testid="confirmation-modal">
        <span>{title}</span>
        <button data-testid="confirm-btn" onClick={onConfirm}>Confirm</button>
        <button data-testid="cancel-btn" onClick={onClose}>Cancel</button>
      </div>
    ) : null,
}));

const defaultProps = {
  id: '1',
  name: 'resume.pdf',
  uploadDate: '15/03/2026',
};

describe('DocumentSection', () => {
  it('renders document name and upload date', () => {
    render(<DocumentSection {...defaultProps} />);
    expect(screen.getByText('resume.pdf')).toBeInTheDocument();
    expect(screen.getByText('Uploaded on 15/03/2026')).toBeInTheDocument();
  });

  it('renders as a button with keyboard access', () => {
    render(<DocumentSection {...defaultProps} />);
    const button = screen.getByRole('button');
    expect(button).toHaveAttribute('tabIndex', '0');
  });

  describe('accessibility', () => {
    it('has aria-label with document name', () => {
      render(<DocumentSection {...defaultProps} />);
      expect(screen.getByRole('button', { name: 'Select resume.pdf' })).toBeInTheDocument();
    });

    it('has aria-label reflecting selected state', () => {
      render(<DocumentSection {...defaultProps} isSelected />);
      expect(screen.getByRole('button', { name: 'Selected: resume.pdf' })).toBeInTheDocument();
    });

    it('sets aria-pressed when selected', () => {
      render(<DocumentSection {...defaultProps} isSelected />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-pressed', 'true');
    });

    it('sets aria-pressed=false when not selected', () => {
      render(<DocumentSection {...defaultProps} />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-pressed', 'false');
    });

    it('hides radio icon from screen readers', () => {
      const { container } = render(<DocumentSection {...defaultProps} />);
      const icon = container.querySelector('[data-icon="radioButtonUnchecked"]');
      expect(icon).toHaveAttribute('aria-hidden', 'true');
    });

    it('renders checked radio icon when selected', () => {
      const { container } = render(<DocumentSection {...defaultProps} isSelected />);
      expect(container.querySelector('[data-icon="radioButtonChecked"]')).toBeInTheDocument();
    });

    it('renders hidden radio input with aria-label when formName is provided', () => {
      render(<DocumentSection {...defaultProps} formName="cv_selection" isSelected />);
      const radio = screen.getByRole('radio', { hidden: true });
      expect(radio).toHaveAttribute('aria-label', 'Select resume.pdf');
    });

    it('renders preview button with aria-label', () => {
      render(<DocumentSection {...defaultProps} previewUrl="/preview/1" />);
      const previewBtn = screen.getByLabelText('Preview resume.pdf');
      expect(previewBtn).toBeInTheDocument();
    });

    it('renders delete button with aria-label', () => {
      render(<DocumentSection {...defaultProps} onDelete={jest.fn()} />);
      const deleteBtn = screen.getByLabelText('Delete resume.pdf');
      expect(deleteBtn).toBeInTheDocument();
    });
  });

  describe('selection', () => {
    it('calls onSelect on click', () => {
      const onSelect = jest.fn();
      render(<DocumentSection {...defaultProps} onSelect={onSelect} />);
      fireEvent.click(screen.getByRole('button'));
      expect(onSelect).toHaveBeenCalledWith('1');
    });

    it('calls onSelect on Enter key', () => {
      const onSelect = jest.fn();
      render(<DocumentSection {...defaultProps} onSelect={onSelect} />);
      fireEvent.keyDown(screen.getByRole('button'), { key: 'Enter' });
      expect(onSelect).toHaveBeenCalledWith('1');
    });

    it('calls onSelect on Space key', () => {
      const onSelect = jest.fn();
      render(<DocumentSection {...defaultProps} onSelect={onSelect} />);
      fireEvent.keyDown(screen.getByRole('button'), { key: ' ' });
      expect(onSelect).toHaveBeenCalledWith('1');
    });
  });

  describe('deletion', () => {
    it('opens confirmation modal on delete click', () => {
      render(<DocumentSection {...defaultProps} onDelete={jest.fn()} />);
      const deleteBtn = screen.getByLabelText('Delete resume.pdf');
      fireEvent.click(deleteBtn);
      expect(screen.getByTestId('confirmation-modal')).toBeInTheDocument();
    });

    it('calls onDelete on confirm when no deleteUrl', () => {
      const onDelete = jest.fn();
      render(<DocumentSection {...defaultProps} onDelete={onDelete} />);
      fireEvent.click(screen.getByLabelText('Delete resume.pdf'));
      fireEvent.click(screen.getByTestId('confirm-btn'));
      expect(onDelete).toHaveBeenCalledWith('1');
    });

    it('closes modal on cancel', () => {
      render(<DocumentSection {...defaultProps} onDelete={jest.fn()} />);
      fireEvent.click(screen.getByLabelText('Delete resume.pdf'));
      expect(screen.getByTestId('confirmation-modal')).toBeInTheDocument();
      fireEvent.click(screen.getByTestId('cancel-btn'));
      expect(screen.queryByTestId('confirmation-modal')).not.toBeInTheDocument();
    });
  });

  describe('preview', () => {
    it('renders preview link with correct href', () => {
      render(<DocumentSection {...defaultProps} previewUrl="/preview/1" />);
      const previewBtn = screen.getByLabelText('Preview resume.pdf');
      expect(previewBtn).toHaveAttribute('href', '/preview/1');
      expect(previewBtn).toHaveAttribute('target', '_blank');
    });

    it('does not render preview button when no previewUrl', () => {
      render(<DocumentSection {...defaultProps} />);
      expect(screen.queryByLabelText('Preview resume.pdf')).not.toBeInTheDocument();
    });
  });

  it('does not render delete button when no onDelete or deleteUrl', () => {
    render(<DocumentSection {...defaultProps} />);
    expect(screen.queryByLabelText('Delete resume.pdf')).not.toBeInTheDocument();
  });

  it('renders delete button when deleteUrl is provided', () => {
    render(<DocumentSection {...defaultProps} deleteUrl="/delete/1" />);
    expect(screen.getByLabelText('Delete resume.pdf')).toBeInTheDocument();
  });
});
